import { useQuery } from "@apollo/client/react";
import { FormattedMessage, useIntl } from "react-intl";
import { MultiSelect } from "@/components/design/MultiSelect";
import { DatePicker } from "@/components/design/DatePicker";
import { TextInput } from "@/components/design/TextField";
import { ContentSharingDocument, DiscoveryCategoriesDocument } from "@/graphql/generated/operations";
import { localizedText, type LocalizedTextLike } from "@/lib/questionnaire-group";
import { userErrorMessage } from "@/lib/user-error";
import { SharedItemSharingFields } from "@/pages/share/SharedItemSharingFields";
import { MAX_RETENTION_DAYS, listingFormErrors, type ListingFormValues } from "./listing-form";

type Recipient = { id: string; name: string; eligible: boolean };
type Category = { id: string; name?: LocalizedTextLike | null };

export type ListingConfigurationSection = "deadline" | "retention" | "categories" | "readership";

export function ListingForm({
  values,
  onChange,
  retentionWasSet,
  section,
  knownRecipients = [],
  knownCategories = [],
}: {
  values: ListingFormValues;
  onChange: (next: ListingFormValues) => void;
  /** Die Ausschreibung hat schon eine Aufbewahrung; leer lassen heißt dann "nicht ändern". */
  retentionWasSet: boolean;
  section: ListingConfigurationSection;
  /** Gruppen, die schon an der Ausschreibung hängen, auch wenn sie nicht mehr wählbar sind. */
  knownRecipients?: readonly Recipient[];
  /** Kategorien, die schon an der Ausschreibung hängen. */
  knownCategories?: readonly Category[];
}) {
  const intl = useIntl();
  const sharing = useQuery(ContentSharingDocument, { fetchPolicy: "network-only", skip: section !== "readership" });
  const categoriesQuery = useQuery(DiscoveryCategoriesDocument, { skip: section !== "categories" });

  const errors = listingFormErrors(values);
  const set = <K extends keyof ListingFormValues>(key: K, value: ListingFormValues[K]) =>
    onChange({ ...values, [key]: value });

  const recipients = new Map<string, Recipient>();
  for (const group of [...knownRecipients, ...(sharing.data?.contentSharingRecipients ?? [])]) {
    recipients.set(group.id, group);
  }
  const categories = new Map<string, Category>();
  for (const category of [...knownCategories, ...(categoriesQuery.data?.discoveryCategories.categories ?? [])]) {
    categories.set(category.id, category);
  }
  const categoryOptions = [...categories.values()]
    .map((category) => ({ value: category.id, label: localizedText(category.name) || category.id }))
    .sort((a, b) => a.label.localeCompare(b.label, intl.locale));
  const onlyMembers = values.sharedWith.length === 0 && !values.publiclyReadable;

  return (
    <div className="space-y-6">
      {section === "deadline" ? (
        <label className="block">
          <span className="smycles-field-label">
            <FormattedMessage id="share.listings.field.deadline" defaultMessage="Frist" />
          </span>
          <DatePicker
            id="listing-form-deadline"
            aria-label={intl.formatMessage({ id: "share.listings.field.deadline" })}
            mode="datetime-local"
            className="mt-1"
            value={values.deadline}
            onChange={(value) => set("deadline", value)}
          />
          <span className="smycles-field-meta mt-1 block text-ink-muted">
            <FormattedMessage
              id="share.listings.form.deadlineHint"
              defaultMessage="Ab der Frist gilt die Ausschreibung als geschlossen. Leer heißt ohne Frist."
            />
          </span>
        </label>
      ) : null}
      {section === "retention" ? (
        <label className="block">
          <span className="smycles-field-label">
            <FormattedMessage id="share.listings.form.retentionDays" defaultMessage="Aufbewahrung in Tagen" />
          </span>
          <TextInput
            id="listing-form-retention"
            type="number"
            inputMode="numeric"
            min={1}
            max={MAX_RETENTION_DAYS}
            step={1}
            className="mt-1"
            value={values.retentionDays}
            onChange={(e) => set("retentionDays", e.target.value)}
            aria-invalid={errors.retentionDays || undefined}
          />
          <span className="smycles-field-meta mt-1 block text-ink-muted">
            {errors.retentionDays ? (
              <FormattedMessage
                id="share.listings.form.retentionInvalid"
                defaultMessage="Bitte eine ganze Zahl von 1 bis 180 eingeben."
              />
            ) : retentionWasSet ? (
              <FormattedMessage id="share.listings.form.retentionHint" defaultMessage="Zählt ab dem Abschluss." />
            ) : (
              <FormattedMessage
                id="share.listings.form.retentionDefaultHint"
                defaultMessage="Zählt ab dem Abschluss. Leer lassen für den Standard."
              />
            )}
          </span>
        </label>
      ) : null}

      {section === "categories" ? <div>
        <span className="smycles-field-label">
          <FormattedMessage id="share.listings.categories" defaultMessage="Kategorien" />
        </span>
        {categoryOptions.length ? (
          <MultiSelect
            id="listing-form-categories"
            className="mt-1"
            options={categoryOptions}
            selected={values.categoryIds}
            onChange={(next) => set("categoryIds", next)}
          />
        ) : (
          <p className="smycles-field-value mt-1 text-ink-muted">
            {categoriesQuery.loading ? (
              <FormattedMessage id="common.loading" defaultMessage="Laden…" />
            ) : categoriesQuery.error ? (
              userErrorMessage(intl, categoriesQuery.error)
            ) : (
              <FormattedMessage id="share.listings.form.noCategories" defaultMessage="Es sind keine Kategorien angelegt." />
            )}
          </p>
        )}
        <span className="smycles-field-meta mt-1 block text-ink-muted">
          <FormattedMessage
            id="share.listings.form.categoriesHint"
            defaultMessage="Kategorien ordnen die Ausschreibung für Bewerbende ein und geben keinen Zugriff."
          />
        </span>
      </div> : null}

      {section === "readership" ? <section className="space-y-2">
        <h2 className="text-smycles-title font-medium">
          <FormattedMessage id="share.listings.form.readershipTitle" defaultMessage="Wer darf die Ausschreibung lesen?" />
        </h2>
        <SharedItemSharingFields
          id="listing-form-sharing"
          selection={{
            ids: values.sharedWith,
            recipients: [...recipients.values()],
            loading: sharing.loading,
            error: sharing.error ? userErrorMessage(intl, sharing.error) : undefined,
            onChange: (ids) => set("sharedWith", ids),
          }}
          publication={{ value: values.publiclyReadable, onChange: (value) => set("publiclyReadable", value) }}
        />
        <p className="smycles-field-meta text-ink-muted">
          {onlyMembers ? (
            <FormattedMessage id="share.listings.readershipMembers" defaultMessage="Nur die Mitglieder." />
          ) : (
            <FormattedMessage
              id="share.listings.readershipHint"
              defaultMessage="Gruppen und Öffentlichkeit erreichen die Ausschreibung nur, solange sie offen ist."
            />
          )}
        </p>
      </section> : null}
    </div>
  );
}
